import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { OndemandVideoOutlined, ForumOutlined, GridViewOutlined, TextFieldsOutlined, ImageOutlined, ViewCarouselOutlined } from '@mui/icons-material';

const CAROUSEL_COLOR = '#c2410c';
const GRID_COLOR     = '#0d7a5f';

const GROUPS = [
  {
    key:   'content',
    label: 'Content',
    color: '#7c3aed',
    items: [
      {
        type:  'text',
        label: 'Text',
        sub:   'Text overlay with background',
        color: '#7c3aed',
        icon:  <TextFieldsOutlined sx={{ fontSize: 22 }} />,
      },
      {
        type:  'image',
        label: 'Image',
        sub:   'Image overlay on the canvas',
        color: '#e67e22',
        icon:  <ImageOutlined sx={{ fontSize: 22 }} />,
      },
    ],
  },
  {
    key:   'livestream',
    label: 'Livestream',
    color: '#EF4444',
    items: [
      {
        type:  'stream',
        label: 'Livestream Player',
        sub:   'Embedded video player',
        color: '#EF4444',
        icon:  <OndemandVideoOutlined sx={{ fontSize: 22 }} />,
        onePerLobby: true,
        disabledInCarousel: true,
      },
      {
        type:  'livestream-chat',
        label: 'Livestream Chat',
        sub:   'Full-height live chat panel',
        color: '#0a7aff',
        icon:  <ForumOutlined sx={{ fontSize: 22 }} />,
        onePerLobby: true,
        disabledInCarousel: true,
      },
    ],
  },
  {
    key:   'exhibitors',
    label: 'Exhibitors',
    color: GRID_COLOR,
    items: [
      {
        type:  'exhibitor-grid',
        label: 'Exhibitor Grid',
        sub:   'Grid of exhibitor booths',
        color: GRID_COLOR,
        icon:  <GridViewOutlined sx={{ fontSize: 22 }} />,
        onePerLobby: true,
      },
    ],
  },
];

function DraggableItem({ item, disabled, disabledLabel = 'Already added', onDragStart, onClick }) {
  return (
    <Box
      draggable={!disabled}
      onDragStart={!disabled ? e => onDragStart(e, item.type) : undefined}
      onClick={!disabled ? () => onClick(item.type) : undefined}
      onMouseDown={e => e.stopPropagation()}
      title={disabled ? disabledLabel : undefined}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 1.25, py: 1.1,
        borderRadius: '10px',
        border: `1px solid ${alpha(item.color, disabled ? 0.08 : 0.18)}`,
        background: alpha(item.color, disabled ? 0.02 : 0.04),
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        userSelect: 'none',
        transition: 'all 0.15s',
        ...(!disabled && {
          '&:hover': {
            background: alpha(item.color, 0.10),
            border: `1px solid ${alpha(item.color, 0.35)}`,
            boxShadow: `0 2px 8px ${alpha(item.color, 0.12)}`,
          },
          '&:active': { cursor: 'grabbing' },
        }),
      }}
    >
      <Box sx={{
        width: 36, height: 36, borderRadius: '9px', flexShrink: 0,
        background: alpha(item.color, disabled ? 0.06 : 0.12),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: item.color,
      }}>
        {item.icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: disabled ? '#b0bac8' : '#1a1a2e', lineHeight: 1.2 }}>
          {item.label}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', mt: 0.2, lineHeight: 1.3 }}>
          {disabled ? disabledLabel : item.sub}
        </Typography>
      </Box>
    </Box>
  );
}

export function ElementPalette({ onDragStart, onClick, existingElementTypes = [], lobbyType }) {
  const isAlreadyCarousel = lobbyType === 'carousel';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2, py: 1.75, borderBottom: '1px solid #e8edf2', flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#5a6474', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Elements
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: '#b0bac8', mt: 0.3 }}>
          Click or drag onto the canvas
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {GROUPS.map(group => (
          <Box key={group.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, px: 0.25 }}>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', background: group.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.63rem', fontWeight: 700, color: group.color, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                {group.label}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {group.items.map(item => {
                const disabled = item.isCarouselItem
                  ? isAlreadyCarousel
                  : item.disabledInCarousel && isAlreadyCarousel
                  ? true
                  : (item.onePerLobby && existingElementTypes.includes(item.type));
                const disabledLabel = item.isCarouselItem
                  ? 'Already a carousel lobby'
                  : item.disabledInCarousel && isAlreadyCarousel
                  ? 'Not available in carousel mode'
                  : 'Already added';
                return (
                  <DraggableItem
                    key={item.type}
                    item={item}
                    disabled={disabled}
                    disabledLabel={disabledLabel}
                    onDragStart={onDragStart}
                    onClick={onClick}
                  />
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}